import { useContext, useRef, type FunctionComponent } from 'react';
import { useMutation } from '@tanstack/react-query';
import QuestionInterface from '../../interfaces/question';
import {
  postMigratePersonalSurveyLastAnswers,
  postQuestionnaireAnswers,
} from '../../services/questionnaire';
import navigationFooterLabels from '../../utils/navigationFooterLabels';
import { getPage, getQuestionIds } from '../../utils/paginator';
import { MappedAnswer, SurveyPageContext } from '../context/SurveyPageContext';
import Layout from '../layout/Layout';
import ProgressBar from '../progress-bar';
import Question from '../questions/Question/Question';
import * as styles from './QuestionPage.module.css';

interface QuestionPageProps {
  questions: QuestionInterface[];
}

const QuestionPage: FunctionComponent<QuestionPageProps> = ({ questions }) => {
  const {
    increasePage,
    decreasePage,
    surveyPage,
    paginatedSurvey,
    mappedAnswers,
    lastButtonAction,
    firstBackButtonAction,
    survey,
  } = useContext(SurveyPageContext);

  const layoutRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: mutation, status: mutationStatus } = useMutation({
    mutationFn: (answers: MappedAnswer[]) => postQuestionnaireAnswers(answers),
  });

  const backAction = {
    label: navigationFooterLabels.BACK,
    disabled: mutationStatus !== 'idle' && mutationStatus !== 'success',
    onClick: () => {
      if (surveyPage === 1 && firstBackButtonAction) {
        firstBackButtonAction();
      } else {
        decreasePage();
        layoutRef.current?.scrollIntoView();
      }
    },
  };

  const currentPage = getPage({
    paginatedSurvey,
    page: surveyPage,
    defaultValue: [],
  });

  if (!survey) return null;

  const currentPageQuestionIds = getQuestionIds({ page: currentPage, survey });

  const lastPage = paginatedSurvey.size <= surveyPage;

  const nextAction = {
    label: lastPage ? navigationFooterLabels.SUBMIT : navigationFooterLabels.NEXT,
    disabled: mutationStatus !== 'idle' && mutationStatus !== 'success',
    onClick: () => {
      const currentPageAnswers = Array.from(mappedAnswers.values()).filter(
        (answer) => answer.question.id && currentPageQuestionIds.includes(answer.question.id),
      );
      mutation(currentPageAnswers).finally(() => {
        if (lastPage) {
          postMigratePersonalSurveyLastAnswers().then(() => lastButtonAction());
        } else {
          increasePage();
          layoutRef.current?.scrollIntoView();
        }
      });
    },
  };

  return (
    <Layout backButton={backAction} nextButton={nextAction} ref={layoutRef}>
      <ProgressBar />
      <div className={styles.questionPageQuestionsContainer}>
        {questions.map((q) => (
          <Question question={q} key={`question-${q.id}`} />
        ))}
      </div>
    </Layout>
  );
};

export default QuestionPage;
